import { useRef, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";

// Campo de upload com preview. `shape` controla o recorte visual do preview
// (não afeta o servidor — backend decide as dimensões reais pelo ImageKind).
//
// Props:
//   value         — URL atual (string) ou null
//   onUpload(file) → Promise<string>  retorna a nova URL
//   onDelete()     → Promise<void>    remove do servidor
//   shape          — "circle" | "rect-16x9" | "rect-4x3" | "rect-1x1"
//   width/height   — preview dimensions em px (default 150x150)
//   label          — rótulo acima
//   hint           — texto auxiliar abaixo
export default function ImageUploadField({
  value,
  onUpload,
  onDelete,
  shape = "rect-4x3",
  width = 200,
  height = 150,
  label,
  hint,
}) {
  const { C } = useTheme();
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handlePick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError("Arquivo excede 5MB.");
      e.target.value = "";
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await onUpload(file);
    } catch (err) {
      setError(err?.response?.data?.message || "Erro ao enviar imagem.");
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  }

  async function handleDelete() {
    if (!value) return;
    setError(null);
    setLoading(true);
    try {
      await onDelete();
    } catch (err) {
      setError(err?.response?.data?.message || "Erro ao remover imagem.");
    } finally {
      setLoading(false);
    }
  }

  const borderRadius = shape === "circle" ? "50%" : 10;
  const aspectRatio =
    shape === "rect-16x9" ? "16/9" :
    shape === "rect-4x3" ? "4/3" :
    shape === "rect-1x1" || shape === "circle" ? "1/1" : "4/3";

  const previewStyle = {
    width,
    height: height ?? "auto",
    aspectRatio: height ? undefined : aspectRatio,
    borderRadius,
    border: `1.5px dashed ${C.border}`,
    background: C.inputBg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: C.textMuted,
    fontSize: 11,
    position: "relative",
    overflow: "hidden",
    flexShrink: 0,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {label && (
        <label style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>
          {label}
        </label>
      )}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={previewStyle}>
          {value ? (
            <img
              src={value}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <span style={{ padding: 10, textAlign: "center" }}>
              {loading ? "Enviando…" : "sem imagem"}
            </span>
          )}
          {loading && value && (
            <div style={{
              position: "absolute", inset: 0,
              background: "rgba(255,255,255,0.7)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, fontWeight: 600, color: C.text,
            }}>
              Enviando…
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={handlePick}
            style={{ display: "none" }}
          />
          <button
            type="button"
            disabled={loading}
            onClick={() => inputRef.current?.click()}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: `1px solid ${C.border}`,
              background: C.bgCard,
              color: C.text,
              fontSize: 12,
              fontWeight: 600,
              cursor: loading ? "wait" : "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {value ? "Trocar" : "Escolher arquivo"}
          </button>
          {value && (
            <button
              type="button"
              disabled={loading}
              onClick={handleDelete}
              style={{
                padding: "6px 14px",
                borderRadius: 8,
                border: `1px solid ${C.red}60`,
                background: "transparent",
                color: C.red,
                fontSize: 11,
                fontWeight: 600,
                cursor: loading ? "wait" : "pointer",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Remover
            </button>
          )}
          {hint && (
            <div style={{ fontSize: 10.5, color: C.textMuted, marginTop: 2 }}>{hint}</div>
          )}
          {error && (
            <div style={{ fontSize: 11, color: C.red, marginTop: 2 }}>{error}</div>
          )}
        </div>
      </div>
    </div>
  );
}
